/**
 * RTK Query API for Parent Portal.
 *
 * Consumes consolidation RPCs + individual mutation RPCs.
 * All Supabase access for the parent portal lives here.
 */
import { createApi, fakeBaseQuery } from "@reduxjs/toolkit/query/react";
import { supabase } from "../../../lib/supabase";
import type {
  ParentPortalInitResponse,
  ConfirmationResponse,
  SetAuthorizedAdultsArgs,
  AddFamilyContactArgs,
  AddFamilyContactResult,
  UpsertContactPhoneArgs,
  UpsertContactPhoneResult,
  SetWeeklyPickupRulesArgs,
  ListPendingDobArgs,
  PendingDobEntry,
  VerifyChildDobArgs,
  VerifyChildDobResult,
  UpdateParentPhonesArgs,
  UpdateParentRelationshipArgs,
} from "./parentPortalTypes";

export const parentPortalApi = createApi({
  reducerPath: "parentPortalApi",
  baseQuery: fakeBaseQuery(),
  tagTypes: [
    "ParentInit",
    "Confirmation",
    "AuthorizedAdults",
    "ParentContacts",
    "ParentRelationships",
    "ChildDetail",
  ],
  endpoints: (builder) => ({
    // ─── Queries ────────────────────────────────────────────

    getParentPortalInit: builder.query<
      ParentPortalInitResponse,
      { profileId: string }
    >({
      async queryFn({ profileId }) {
        try {
          if (!profileId) {
            return { error: { status: 400, data: "Missing profileId" } };
          }
          const { data, error } = await supabase.rpc("fn_parent_portal_init", {
            p_user_profile_id: profileId,
          });
          if (error) {
            return { error: { status: 500, data: error.message } };
          }
          return { data: data as ParentPortalInitResponse };
        } catch (err) {
          return { error: { status: 500, data: String(err) } };
        }
      },
      providesTags: (_result, _error, { profileId }) => [
        { type: "ParentInit", id: profileId },
      ],
    }),

    getConfirmation: builder.query<
      ConfirmationResponse,
      { profileId: string; draftId: string }
    >({
      async queryFn({ profileId, draftId }) {
        try {
          if (!profileId || !draftId) {
            return {
              error: { status: 400, data: "Missing profileId or draftId" },
            };
          }
          const { data, error } = await supabase.rpc(
            "fn_parent_portal_get_confirmation",
            {
              p_user_profile_id: profileId,
              p_draft_id: draftId,
            },
          );
          if (error) {
            return { error: { status: 500, data: error.message } };
          }
          return { data: data as ConfirmationResponse };
        } catch (err) {
          return { error: { status: 500, data: String(err) } };
        }
      },
      providesTags: (_result, _error, { draftId }) => [
        { type: "Confirmation", id: draftId },
      ],
    }),

    listPendingDobVerifications: builder.query<
      PendingDobEntry[],
      ListPendingDobArgs
    >({
      async queryFn({ parentProfileId, targetChildId }) {
        try {
          if (!parentProfileId) return { data: [] };
          const { data, error } = await supabase.rpc(
            "list_pending_dob_verifications",
            { p_parent_profile_id: parentProfileId },
          );
          if (error) {
            return { error: { status: 500, data: error.message } };
          }
          // The legacy RPC returns snake_case columns (link_id, child_profile_id, child_name);
          // map to the camelCase PendingDobEntry shape used everywhere else.
          let rows: PendingDobEntry[] = Array.isArray(data)
            ? (data as Array<{
                link_id: string;
                child_profile_id: string;
                child_name: string;
              }>).map((r) => ({
                linkId: r.link_id,
                childProfileId: r.child_profile_id,
                childName: r.child_name,
              }))
            : [];
          if (targetChildId) {
            rows = rows.filter((r) => r.childProfileId === targetChildId);
          } else if (rows.length > 1) {
            rows = rows.slice(0, 1);
          }
          return { data: rows };
        } catch (err) {
          return { error: { status: 500, data: String(err) } };
        }
      },
    }),

    // ─── Mutations ──────────────────────────────────────────

    setAuthorizedAdults: builder.mutation<void, SetAuthorizedAdultsArgs>({
      async queryFn({ childId, adultIds, schoolId }) {
        try {
          const { error } = await supabase.rpc("parent_set_authorized_adults", {
            p_child_id: childId,
            p_adult_ids: adultIds,
          } as any);
          if (error) {
            return { error: { status: 500, data: error.message } };
          }
          return { data: undefined };
        } catch (err) {
          return { error: { status: 500, data: String(err) } };
        }
      },
    }),

    addFamilyContact: builder.mutation<
      AddFamilyContactResult,
      AddFamilyContactArgs
    >({
      async queryFn({
        childId,
        familyId,
        firstName,
        lastName,
        relationship,
        phone,
        authorizedForPickup,
      }) {
        try {
          const { error } = await supabase.rpc(
            "add_family_contact_for_student_react",
            {
              _student_id: childId,
              _family_id: familyId,
              _first_name: firstName,
              _last_name: lastName,
              _phone: phone || "",
              _relationship: relationship,
              _authorized_for_pickup: authorizedForPickup ?? true,
            } as any,
          );
          if (error) {
            return { error: { status: 500, data: error.message } };
          }
          return { data: { success: true } };
        } catch (err) {
          return { error: { status: 500, data: String(err) } };
        }
      },
    }),

    upsertContactPhone: builder.mutation<
      UpsertContactPhoneResult,
      UpsertContactPhoneArgs
    >({
      async queryFn({ childId, adultProfileId, contactId, phone, label }) {
        try {
          const { data, error } = await supabase.rpc(
            "fn_contacts_parent_upsert_phone",
            {
              p_child_profile_id: childId,
              p_adult_profile_id: adultProfileId,
              p_contact_id: contactId ?? null,
              p_phone_input: phone,
              p_label: label ?? null,
            } as any,
          );
          if (error) {
            return {
              error: {
                status: 500,
                data:
                  error.message || "Fehler beim Aktualisieren des Kontakts.",
              },
            };
          }
          let resultContactId: string | undefined;
          if (typeof data === "string") {
            resultContactId = data;
          } else if (Array.isArray(data) && data.length) {
            resultContactId = String(data[0]);
          } else if (data && typeof data === "object") {
            const vals = Object.values(data as Record<string, unknown>).filter(
              (v) => typeof v === "string" || typeof v === "number",
            );
            if (vals.length) resultContactId = String(vals[0]);
          }
          return { data: { success: true, contactId: resultContactId } };
        } catch (err: any) {
          return {
            error: {
              status: 500,
              data:
                err?.message ||
                "Unbekannter Fehler beim Aktualisieren des Kontakts.",
            },
          };
        }
      },
    }),

    setWeeklyPickupRules: builder.mutation<void, SetWeeklyPickupRulesArgs>({
      // Invalidate the child-detail cache for this student so the hook
      // refetches fresh pickup rules after save. Without this the cache
      // keeps the pre-save rules and other parts of the UI show stale data.
      invalidatesTags: (_r, _e, { childId }) => [{ type: "ChildDetail", id: childId }],
      async queryFn({ childId, rules }) {
        try {
          const { error } = await supabase.rpc(
            "parent_set_weekly_pickup_rules",
            {
              p_student_id: childId,
              p_rules: rules,
            } as any,
          );
          if (error) {
            return { error: { status: 500, data: error.message } };
          }
          return { data: undefined };
        } catch (err) {
          return { error: { status: 500, data: String(err) } };
        }
      },
    }),

    verifyChildDob: builder.mutation<VerifyChildDobResult, VerifyChildDobArgs>({
      async queryFn({ childId, dob }) {
        try {
          const { data, error } = await supabase.rpc(
            "verify_parent_child_dob",
            {
              p_child_id: childId,
              p_dob: dob,
            },
          );
          if (error) {
            return { error: { status: 500, data: error.message } };
          }
          const res = Array.isArray(data) ? data[0] : data;
          return { data: res as VerifyChildDobResult };
        } catch (err) {
          return { error: { status: 500, data: String(err) } };
        }
      },
    }),

    updateParentPhones: builder.mutation<void, UpdateParentPhonesArgs>({
      async queryFn({ parentProfileId, contacts }) {
        try {
          const { error } = await supabase.rpc(
            "fn_families_update_parent_phones",
            {
              p_parent_profile_id: parentProfileId,
              p_contacts: contacts as any,
            },
          );
          if (error) {
            return { error: { status: 500, data: error.message } };
          }
          return { data: undefined };
        } catch (err) {
          return { error: { status: 500, data: String(err) } };
        }
      },
    }),

    updateParentRelationship: builder.mutation<
      void,
      UpdateParentRelationshipArgs
    >({
      async queryFn({ schoolId, parentProfileId, childId, relationship }) {
        try {
          const { error } = await supabase
            .from("family_member_child_links" as any)
            .update({ relationship: relationship || null })
            .eq("school_id", schoolId)
            .eq("adult_profile_id", parentProfileId)
            .eq("child_profile_id", childId);
          if (error) {
            return { error: { status: 500, data: error.message } };
          }
          return { data: undefined };
        } catch (err) {
          return { error: { status: 500, data: String(err) } };
        }
      },
    }),

    // ─── Parent Phone Contacts ────────────────────────────

    getParentPhoneContacts: builder.query<
      { value: string; label: string }[],
      { profileId: string; schoolId: string }
    >({
      async queryFn({ profileId, schoolId }) {
        try {
          const { data, error } = await (supabase as any)
            .from("contacts")
            .select("value, type, label, status")
            .eq("profile_id", profileId)
            .eq("school_id", schoolId)
            .neq("status", "archived");
          if (error) return { error: { status: 500, data: error.message } };
          const phoneTypes = new Set([
            "phone",
            "mobile",
            "work_phone",
            "home_phone",
            "notfall",
            "telefon",
            "telefonnummer",
            "handy",
            "tel",
            "festnetz",
          ]);
          const vals = (data || [])
            .filter((r: any) => {
              const t = (r?.type || "").toString().toLowerCase();
              const v = (r?.value || "").toString();
              return phoneTypes.has(t) || /[+\d][\d\s()./-]{4,}/.test(v);
            })
            .filter((r: any) => typeof r?.value === "string" && r.value.trim())
            .map((r: any) => ({
              value: r.value as string,
              label: (r.label || "Privat") as string,
            }));
          return { data: vals };
        } catch (err) {
          return { error: { status: 500, data: String(err) } };
        }
      },
      providesTags: (_r, _e, { profileId }) => [
        { type: "ParentContacts", id: profileId },
      ],
    }),

    // ─── Parent Child Relationships ───────────────────────

    getParentChildRelationships: builder.query<
      Record<string, string>,
      { profileId: string; schoolId: string; childIds: string[] }
    >({
      async queryFn({ profileId, schoolId, childIds }) {
        try {
          if (!childIds.length) return { data: {} };
          const { data: links, error } = await (supabase as any)
            .from("family_member_child_links")
            .select("child_profile_id, relationship")
            .eq("school_id", schoolId)
            .eq("adult_profile_id", profileId)
            .in("child_profile_id", childIds);
          if (error) return { error: { status: 500, data: error.message } };
          const relMap: Record<string, string> = {};
          (links || []).forEach((l: any) => {
            if (l?.child_profile_id)
              relMap[String(l.child_profile_id)] = l.relationship || "";
          });
          return { data: relMap };
        } catch (err) {
          return { error: { status: 500, data: String(err) } };
        }
      },
      providesTags: (_r, _e, { profileId }) => [
        { type: "ParentRelationships", id: profileId },
      ],
    }),

    // ─── Child Detail (consolidated RPC) ────────────────

    getChildDetail: builder.query<any, { profileId: string; childId: string }>({
      async queryFn({ profileId, childId }) {
        try {
          if (!profileId || !childId) {
            return {
              error: { status: 400, data: "Missing profileId or childId" },
            };
          }
          const { data, error } = await supabase.rpc("fn_parent_child_detail", {
            p_user_profile_id: profileId,
            p_child_id: childId,
          });
          if (error) {
            return { error: { status: 500, data: error.message } };
          }
          // RPC returns errors as { error: string } inside data
          if (data?.error) {
            return { error: { status: 403, data: data.error } };
          }
          return { data };
        } catch (err) {
          return { error: { status: 500, data: String(err) } };
        }
      },
      providesTags: (_result, _error, { childId }) => [
        { type: "ChildDetail", id: childId },
        { type: "AuthorizedAdults", id: childId },
      ],
    }),

    // ─── Sick Report Submission ────────────────────────

    submitSickReport: builder.mutation<
      { successCount: number; failedCount: number },
      {
        studentIds: string[];
        schoolId: string;
        creatorId: string;
        absenceDate: string;
        endDate?: string;
        reason: string;
        absenceStatus: string;
        isMultipleDays: boolean;
      }
    >({
      async queryFn({
        studentIds,
        schoolId,
        creatorId,
        absenceDate,
        endDate,
        reason,
        absenceStatus,
        isMultipleDays,
      }) {
        try {
          const recurrence =
            isMultipleDays && endDate
              ? {
                  startDate: absenceDate,
                  endDate,
                  repeatEveryUnit: "day",
                  repeatEveryNumber: 1,
                  weekDays: [] as number[],
                }
              : null;

          const calls = studentIds.map((studentId) =>
            supabase.rpc("add_student_absence_with_times_and_recurrence", {
              p_student_id: studentId,
              p_school_id: schoolId,
              p_absence_date: absenceDate,
              p_from_time: "00:00:00",
              p_to_time: "23:59:59",
              p_reason: reason,
              p_excused: false,
              p_attachment_url: null,
              p_created_by: creatorId,
              p_recurrence: recurrence,
              p_absence_status: absenceStatus,
            } as any),
          );

          const results = await Promise.all(calls);
          const failed = results.filter((r: any) => r?.error);

          if (failed.length) {
            const messages = failed
              .map((f: any) => f.error?.message || JSON.stringify(f.error))
              .join(" | ");
            return { error: { status: 500, data: messages } };
          }

          // Stamp origin (non-fatal)
          try {
            const endIso = isMultipleDays && endDate ? endDate : absenceDate;
            await supabase
              .from("student_absence_notes" as any)
              .update({
                submitted_via: "parent_portal",
              } as any)
              .in("student_id", studentIds as any)
              .eq("school_id", schoolId as any)
              .eq("created_by", creatorId as any)
              .lte("start_date", endIso as any)
              .gte("end_date", absenceDate as any);

            await supabase
              .from("student_daily_log" as any)
              .update({
                created_via: "parent_portal",
                created_by_profile: creatorId,
              } as any)
              .in("student_id", studentIds as any)
              .eq("school_id", schoolId as any)
              .gte("date", absenceDate as any)
              .lte("date", endIso as any);
          } catch {}

          return {
            data: {
              successCount: studentIds.length - failed.length,
              failedCount: failed.length,
            },
          };
        } catch (err) {
          return { error: { status: 500, data: String(err) } };
        }
      },
      invalidatesTags: ["ParentInit"],
    }),

    // ─── Add Child by Access Code ──────────────────────

    addChildByCode: builder.mutation<
      { childId: string | null; result: any },
      {
        code: string;
        childDob: string;
        relationship: string;
        profileId: string;
        schoolId: string;
      }
    >({
      async queryFn({ code, childDob, relationship, profileId, schoolId }) {
        try {
          const { data, error } = await supabase.rpc(
            "add_child_by_code_react",
            {
              p_code: code,
              p_child_dob: childDob,
              p_relationship: relationship || "parent",
            },
          );

          if (error) {
            return { error: { status: 500, data: error.message } };
          }

          const payloadRaw = data as any;
          const result = Array.isArray(payloadRaw) ? payloadRaw[0] : payloadRaw;
          if (!result || result.success === false) {
            const msg =
              result?.error?.message || result?.error || JSON.stringify(result);
            return { error: { status: 400, data: String(msg) } };
          }

          // Extract child ID from various response shapes
          let childId: string | null = null;
          const potentialChildId =
            result.child_profile_id ||
            result.child_id ||
            result.profile_id ||
            result.child ||
            null;
          if (typeof potentialChildId === "string") {
            childId = potentialChildId;
          } else if (potentialChildId && typeof potentialChildId === "object") {
            childId =
              potentialChildId.id || potentialChildId.child_profile_id || null;
          }

          // Flip access_restricted → false (non-fatal)
          try {
            if (profileId && schoolId && childId) {
              await supabase
                .from("family_member_child_links")
                .update({ access_restricted: false })
                .eq("adult_profile_id", profileId)
                .eq("child_profile_id", childId)
                .eq("school_id", schoolId);
            }
          } catch {}

          return { data: { childId, result } };
        } catch (err) {
          return { error: { status: 500, data: String(err) } };
        }
      },
      invalidatesTags: ["ParentInit"],
    }),

    /**
     * Returns one entry per child of the caller for which today's
     * distribution.stay_home roster includes them — a small payload used
     * to render an action-required / decided badge on the child card in
     * "Deine Kinder". Empty array on no events. Server-side date scoping
     * uses Europe/Berlin.
     */
    getTodayChildStayHome: builder.query<
      Array<{
        student_id: string;
        child_first_name: string | null;
        child_last_name: string | null;
        lesson_id: string;
        source_name: string | null;
        subject_name: string | null;
        time_from: string | null;
        time_to: string | null;
        lesson_date: string;
        has_answered: boolean;
        decision: "go_home" | "stay_at_school" | null;
        pickup_mode: "picked_up" | "walks_alone" | null;
        decision_message: string | null;
        decided_at: string | null;
      }>,
      { forDate?: string | null } | void
    >({
      async queryFn(arg) {
        try {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const { data, error } = await (supabase as any).rpc(
            "fn_parent_get_today_child_stay_home",
            { p_for_date: arg?.forDate ?? null },
          );
          if (error)
            return { error: { status: 500, data: error.message } };
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          return { data: (data as any[]) ?? [] };
        } catch (err) {
          return { error: { status: 500, data: String(err) } };
        }
      },
      providesTags: ["ParentInit"],
    }),
  }),
});

export const {
  useGetParentPortalInitQuery,
  useGetTodayChildStayHomeQuery,
  useGetConfirmationQuery,
  useListPendingDobVerificationsQuery,
  useGetParentPhoneContactsQuery,
  useGetParentChildRelationshipsQuery,
  useGetChildDetailQuery,
  useSetAuthorizedAdultsMutation,
  useAddFamilyContactMutation,
  useUpsertContactPhoneMutation,
  useSetWeeklyPickupRulesMutation,
  useVerifyChildDobMutation,
  useUpdateParentPhonesMutation,
  useUpdateParentRelationshipMutation,
  useSubmitSickReportMutation,
  useAddChildByCodeMutation,
} = parentPortalApi;
